import type { BootstrapResponse } from '../api/bootstrap';
import { BRAND_NAME } from '../brand';
import type { SubscriptionSnapshot } from '../api/subscription';

export const TRIAL_ENDING_SOON_DAYS = 3;

export function getSubscriptionSnapshot(bootstrap: BootstrapResponse | null | undefined): SubscriptionSnapshot | null {
    return bootstrap?.subscription ?? bootstrap?.user?.subscription ?? null;
}

export function trialEndingSoonDaysLeft(subscription: SubscriptionSnapshot | null | undefined): number | null {
    if (!subscription || subscription.status !== 'trial') return null;
    const days = subscription.trial_days_left;
    return typeof days === 'number' && Number.isFinite(days) ? days : null;
}

/** Shell + Billing: show urgent trial banner when this many days or fewer remain. */
export function isTrialEndingSoon(subscription: SubscriptionSnapshot | null | undefined): boolean {
    const days = trialEndingSoonDaysLeft(subscription);
    return days !== null && days <= TRIAL_ENDING_SOON_DAYS;
}

export function trialEndingSoonBannerLine(daysLeft: number): string {
    return `Your trial ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}. Free during beta — keep using ${BRAND_NAME}. Paid Pro comes after beta.`;
}

/** Active trial with more than TRIAL_ENDING_SOON_DAYS days left — Billing-only calm status. */
export function trialActiveCalmLine(daysLeft: number): string {
    return `Pro trial active — ${daysLeft} day${daysLeft === 1 ? '' : 's'} remaining.`;
}

export function formatAccessUntilLabel(accessUntil: string | null | undefined): string {
    if (!accessUntil) return 'period end';
    const parsed = new Date(accessUntil);
    return Number.isNaN(parsed.getTime()) ? 'period end' : parsed.toLocaleDateString();
}

export function cancellationScheduledLine(accessUntilLabel: string): string {
    return `Pro cancellation is scheduled. Access stays active until ${accessUntilLabel}. Manage your subscription on Billing.`;
}

/**
 * Plan profile says Pro but effective access is Free (e.g. expired trial) — do not imply Pro access.
 */
export function trialEndedEffectiveFreeLine(plan: string | undefined | null, effectivePlan: string | undefined | null): string | null {
    const p = (plan || 'free').toLowerCase();
    const e = (effectivePlan || 'free').toLowerCase();
    if (p === 'pro' && e === 'free') {
        return `Free during beta — keep using ${BRAND_NAME}. Paid Pro comes after beta.`;
    }
    return null;
}

export function profilePlanFootnote(
    plan: string | undefined | null,
    effectivePlan: string | undefined | null,
    trialExpiresAt: string | null | undefined,
    subscription: SubscriptionSnapshot | null,
): string {
    const ended = trialEndedEffectiveFreeLine(plan, effectivePlan);
    if (ended) {
        return ended;
    }

    if (subscription?.status === 'trial' && typeof subscription.trial_days_left === 'number') {
        return `Pro trial: ${subscription.trial_days_left} day${subscription.trial_days_left === 1 ? '' : 's'} left.`;
    }

    if (trialExpiresAt) {
        const dt = new Date(trialExpiresAt);
        if (!Number.isNaN(dt.getTime())) {
            return `Trial window ends ${dt.toLocaleString()}.`;
        }
    }

    return `Plan ${(plan || 'free').toUpperCase()} · Effective ${(effectivePlan || 'free').toUpperCase()}`;
}
